import type { RuntimeState } from "@/types/platform";

/**
 * Feature B — the cross-mode runtime lifecycle STATE badge (Docker-Desktop-
 * blade style). Colours: green healthy/running · amber unhealthy/restarting ·
 * blue provisioning · red exited/missing · grey stopped/orphan. Mirrors the
 * lexserver `RuntimeState` (runtime-state.ts) and is the cross-UI source of
 * truth the LexIDE connection icon (Feature C) matches.
 */
const STATE_STYLE: Record<RuntimeState, { label: string; cls: string; dot: string }> = {
  healthy: {
    label: "Healthy",
    cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
  running: {
    label: "Running",
    cls: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300",
    dot: "bg-emerald-400",
  },
  unhealthy: {
    label: "Unhealthy",
    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    dot: "bg-amber-500",
  },
  restarting: {
    label: "Restarting",
    cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    dot: "bg-amber-500",
  },
  provisioning: {
    label: "Provisioning",
    cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    dot: "bg-blue-500",
  },
  exited: {
    label: "Exited",
    cls: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
    dot: "bg-red-500",
  },
  missing: {
    label: "Missing",
    cls: "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
    dot: "bg-red-500",
  },
  stopped: {
    label: "Stopped",
    cls: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    dot: "bg-slate-400",
  },
  orphan: {
    label: "Orphan",
    cls: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    dot: "bg-slate-400",
  },
};

export function RuntimeStateBadge({ state }: { state?: RuntimeState }) {
  if (!state) {
    return <span className="text-slate-400">—</span>;
  }
  const s = STATE_STYLE[state] ?? {
    label: state,
    cls: "bg-slate-100 text-slate-600",
    dot: "bg-slate-400",
  };
  return (
    <span
      data-testid={`runtime-state-${state}`}
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${s.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}
